import {ElementRef, inject, Injectable, PLATFORM_ID} from '@angular/core';
import {IconConfig, ZoomSpec} from './map.types';
import {DEFAULT_ZOOM_SPEC, MAP_TILE_CONFIG} from './map.constants';
import {isPlatformBrowser} from '@angular/common';

@Injectable({
  providedIn: 'root'
})
export class MapService {
  private readonly maps = new Map<string, L.Map>();
  private readonly geoJsonLayers = new Map<string, L.GeoJSON>();
  private L: typeof import('leaflet') | null = null;
  private readonly platformId = inject(PLATFORM_ID);
  protected readonly isBrowser = isPlatformBrowser(this.platformId)

  private async ensureLeaflet(): Promise<any> {
    if (!this.isBrowser) throw new Error('Leaflet cannot be used in SSR');

    if (!this.L) {
      const module = await import('leaflet');
      this.L = module.default ?? module;
    }

    return this.L;
  }

  public async init(
    id: string,
    containerRef: ElementRef,
    coords: L.LatLngExpression,
    zoomSpec: ZoomSpec = DEFAULT_ZOOM_SPEC,
    scrollable = true,
    draggable = true
  ): Promise<void> {
    if (!this.isBrowser) return;

    const L = await this.ensureLeaflet();

    if (this.maps.has(id)) {
      this.maps.get(id)?.remove();
      this.maps.delete(id);
    }

    const map = L.map(containerRef.nativeElement, {
      zoomControl: zoomSpec.zoomControl ?? false,
      minZoom: zoomSpec.minZoom,
      maxZoom: zoomSpec.maxZoom,
      scrollWheelZoom: scrollable,
      dragging: draggable
    }).setView(coords, zoomSpec.zoomLevel);

    if (zoomSpec.zoomControl && zoomSpec.zoomControlPosition) {
      L.control.zoom({position: zoomSpec.zoomControlPosition}).addTo(map);
    }

    L.tileLayer(MAP_TILE_CONFIG.url, MAP_TILE_CONFIG.options).addTo(map);

    this.maps.set(id, map);
  }

  public async addMarker(id: string, config: IconConfig): Promise<L.Marker | null> {
    if (!this.isBrowser) return null;

    const L = await this.ensureLeaflet();

    const map = this.maps.get(id);
    if (!map) return null;

    const icon = L.icon({
      ...config.base,
      iconAnchor: this.resolveMarkerAnchor(config)
    });

    return L.marker(config.coordinates, {icon}).addTo(map);
  }

  public async addGeoJsonBoundaries(id: string, geoJsonData: any, style?: L.PathOptions): Promise<L.GeoJSON | null> {
    if (!this.isBrowser) return null;

    const map = this.maps.get(id);
    if (!map) return null;

    if (this.geoJsonLayers.has(id)) {
      this.geoJsonLayers.get(id)?.remove();
    }

    const L = await this.ensureLeaflet();

    const boundaries = L.geoJSON(geoJsonData, {
      style: style ?? {color: '#007bff', weight: 2, fillOpacity: 0.05}
    }).addTo(map);

    map.fitBounds(boundaries.getBounds(), {padding: [20, 20]});
    this.geoJsonLayers.set(id, boundaries);

    return boundaries;
  }

  public getMaps() {
    return this.maps;
  }

  public getMap(id: string): L.Map | undefined {
    return this.maps.get(id);
  }

  public destroyMap(id: string): void {
    if (!this.isBrowser) return;

    this.geoJsonLayers.get(id)?.remove();
    this.maps.get(id)?.remove();
    this.maps.delete(id);
    this.geoJsonLayers.delete(id);
  }

  public destroyAll(): void {
    if (!this.isBrowser) return;

    this.geoJsonLayers.forEach(layer => layer.remove());
    this.maps.forEach(map => map.remove());
    this.maps.clear();
    this.geoJsonLayers.clear();
  }

  private resolveMarkerAnchor(config: IconConfig): L.PointExpression {
    const [width, height] = config.base.iconSize as [number, number];
    return [width / 2, height];
  }
}
